import { Box, IconButton, Theme, Toolbar, makeStyles } from '@material-ui/core';
import { saveAs } from 'file-saver';
// eslint-disable-next-line import/no-extraneous-dependencies, import/extensions
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';
import { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import MinusIcon from '../assets/images/minus-icon-light.svg';
import PlusIcon from '../assets/images/plus-icon-light.svg';
import PrintIcon from '../assets/images/print-icon.svg';
import RefreshIcon from '../assets/images/refresh-icon.svg';
import DownloadIcon from '../assets/images/download-file-icon.svg';

interface MakeStylesProps {
  modal: boolean;
  zoom: number;
}

const useStyles = makeStyles<Theme, MakeStylesProps>((theme) => ({
  button: {
    height: 60,
    width: 60,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
  },

  content: {
    flex: 1,
    overflow: 'auto',
  },
  controls: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
  document: ({ zoom }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    zoom: `${zoom}%`,
  }),
  toolbar: ({ modal }) => ({
    ...(modal
      ? {
          marginLeft: -40,
          marginRight: -40,
        }
      : {}),
    borderBottom: `1px solid ${
      theme.palette.type === 'light'
        ? theme.palette.common.greyMid
        : theme.palette.common.backgroundNotification
    }`,
    borderTop: modal
      ? 0
      : `1px solid ${
          theme.palette.type === 'light'
            ? theme.palette.common.greyMid
            : theme.palette.common.backgroundNotification
        }`,
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingTop: modal ? 0 : theme.spacing(2),
  }),
  whiteIcon: {
    filter: theme.palette.type === 'light' ? 'invert(1)' : 'none',
  },
  zoomLevel: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.greyLighter,
    color: theme.palette.common.white,
    display: 'flex',
    fontSize: theme.typography.body2.fontSize,
    height: 35,
    justifyContent: 'center',
    width: 65,
  },
}));

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

interface PdfViewerProps {
  file: Blob | null;

  modal?: boolean;

  name: string;
}

const options = {
  cMapUrl: '/cmaps/',
  standardFontDataUrl: '/standard_fonts/',
};

function PdfViewer({ file, modal = false, name }: PdfViewerProps) {
  const [pages, setPages] = useState(0);

  const [zoom, setZoom] = useState(100);

  const [rotate, setRotate] = useState(0);

  const classes = useStyles({
    modal,
    zoom,
  });

  const download = () => {
    if (file) {
      saveAs(file, `${name}.pdf`);
    }
  };

  const print = () => {
    if (file) {
      const iframe = document.createElement('iframe');

      iframe.style.display = 'none';

      iframe.src = URL.createObjectURL(file);

      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          iframe.focus();
          iframe.contentWindow?.print();
        }, 1);
      };
    }
  };

  const rotateDocument = () => {
    setRotate(rotate === 270 ? 0 : rotate + 90);
  };

  const zoomIn = () => {
    setZoom(zoom + 10);
  };

  const zoomOut = () => {
    setZoom(zoom - 10);
  };

  return (
    <Box className={classes.container}>
      <Toolbar className={classes.toolbar}>
        <Box className={classes.controls}>
          <IconButton className={classes.button} onClick={zoomOut}>
            <img src={MinusIcon} alt="Decrease zoom by 10%" />
          </IconButton>

          <Box className={classes.zoomLevel}>{zoom}%</Box>

          <IconButton className={classes.button} onClick={zoomIn}>
            <img src={PlusIcon} alt="Increase zoom by 10%" />
          </IconButton>

          <IconButton className={classes.button} onClick={rotateDocument}>
            <img src={RefreshIcon} alt="Rotate document" />
          </IconButton>
        </Box>

        <Box className={classes.controls}>
          <IconButton className={classes.button} onClick={print}>
            <img
              className={classes.whiteIcon}
              src={PrintIcon}
              alt="Print document"
            />
          </IconButton>

          <IconButton className={classes.button} onClick={download}>
            <img
              className={classes.whiteIcon}
              src={DownloadIcon}
              alt="Download document"
            />
          </IconButton>
        </Box>
      </Toolbar>

      <Box className={classes.content}>
        <Document
          className={classes.document}
          file={file}
          options={options}
          rotate={rotate}
          onLoadSuccess={({ numPages }) => {
            setPages(numPages);
          }}
        >
          {Array.from(new Array(pages), (_, index) => (
            <Page key={`page_${index + 1}`} pageNumber={index + 1} />
          ))}
        </Document>
      </Box>
    </Box>
  );
}

export default PdfViewer;
